import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Plus, UserCircle, Search, Pencil, Trash2, Calendar, Heart, AlertTriangle, Mail, Eye } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { GuestProfileDialog } from '../../components/user/GuestProfileDialog';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const GuestProfiling = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [user, setUser] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchProfiles(parsedUser.entityId);
    }
  }, []);

  const fetchProfiles = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/guest-profiles/${propertyId}`);
      if (response.data.success) {
        setProfiles(response.data.profiles);
        setFilteredProfiles(response.data.profiles);
      }
    } catch (error) {
      console.error('Error fetching guest profiles:', error);
      toast({
        title: "Error",
        description: "Failed to fetch guest profiles",
        variant: "destructive"
      });
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term === '') {
      setFilteredProfiles(profiles);
    } else {
      const filtered = profiles.filter(profile => 
        profile.firstName.toLowerCase().includes(term) ||
        profile.lastName.toLowerCase().includes(term) ||
        (profile.email && profile.email.toLowerCase().includes(term))
      );
      setFilteredProfiles(filtered);
    }
  };

  const handleSave = async (data) => {
    try {
      let response;
      
      if (selectedProfile) {
        response = await axios.put(
          `${BACKEND_URL}/api/guest-profiles/${selectedProfile.id}`,
          data
        );
      } else {
        response = await axios.post(
          `${BACKEND_URL}/api/guest-profiles`,
          {
            propertyId: user.entityId,
            ...data
          }
        );
      }
      
      setIsDialogOpen(false);
      setSelectedProfile(null);
      fetchProfiles(user.entityId);
      
      toast({
        title: "Success",
        description: selectedProfile ? "Guest profile updated successfully" : "Guest profile created successfully"
      });
      
    } catch (error) {
      console.error('Error saving guest profile:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save guest profile",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (profile) => {
    setSelectedProfile(profile);
    setIsDialogOpen(true);
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this guest profile?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/guest-profiles/${profileId}`);
      fetchProfiles(user.entityId);
      toast({
        title: "Success",
        description: "Guest profile deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting guest profile:', error);
      toast({
        title: "Error",
        description: "Failed to delete guest profile",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Guest Profiling</h1>
            <p className="text-slate-600 mt-1">Manage guest preferences and details</p>
          </div>
          <Button
            onClick={() => {
              setSelectedProfile(null);
              setIsDialogOpen(true);
            }}
            className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Guest Profile
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>

        {/* Profiles Grid */}
        {filteredProfiles.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <UserCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Guest Profiles</h3>
            <p className="text-slate-500 mb-4">Start by adding your first guest profile</p>
            <Button
              onClick={() => {
                setSelectedProfile(null);
                setIsDialogOpen(true);
              }}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Guest Profile
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProfiles.map((profile) => (
              <div
                key={profile.id}
                className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">
                        {profile.firstName} {profile.lastName}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {profile.numVisits || 0} visits
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/user/guest-profile/${profile.id}`)}
                      className="h-8 w-8 p-0"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4 text-teal-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(profile)}
                      className="h-8 w-8 p-0"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(profile.id)}
                      className="h-8 w-8 p-0"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {profile.email && (
                    <div className="flex items-start space-x-2">
                      <Mail className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600 line-clamp-1">{profile.email}</span>
                    </div>
                  )}
                  {profile.allergies && (
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600 line-clamp-1">{profile.allergies}</span>
                    </div>
                  )}
                  {profile.preferences && (
                    <div className="flex items-start space-x-2">
                      <Heart className="w-4 h-4 text-pink-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600 line-clamp-1">{profile.preferences}</span>
                    </div>
                  )}
                  {(profile.birthday || profile.anniversary) && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-600">
                        {profile.birthday && `B: ${formatDate(profile.birthday)}`}
                        {profile.birthday && profile.anniversary && ' | '}
                        {profile.anniversary && `A: ${formatDate(profile.anniversary)}`}
                      </span>
                    </div>
                  )}
                </div>

                {(profile.favouriteDrink || profile.favouriteFood) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex flex-wrap gap-2">
                      {profile.favouriteDrink && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                          🍹 {profile.favouriteDrink}
                        </span>
                      )}
                      {profile.favouriteFood && (
                        <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-xs">
                          🍽️ {profile.favouriteFood}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <GuestProfileDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        profile={selectedProfile}
        onSave={handleSave}
      />
    </UserLayout>
  );
};
